import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "../../lib/utils"

const buttonVariants = cva(
  "eq-press inline-flex shrink-0 items-center justify-center gap-2 rounded-[8px] text-sm font-semibold whitespace-nowrap outline-none focus-visible:border-[color:var(--eq-gold)] focus-visible:ring-[3px] focus-visible:ring-[rgba(223,192,106,0.45)] disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default: "bg-[color:var(--eq-navy)] text-white hover:bg-[#152A42]",
        // The single most important action on a screen — lift lives in globals.css (.eq-btn-gold).
        gold: "eq-btn-gold bg-[color:var(--eq-gold)] text-[color:var(--eq-navy)]",
        // Navy, never red; data-destructive lets a dialog spell out the confirmation.
        destructive: "bg-[color:var(--eq-navy)] text-white hover:bg-[#152A42]",
        outline:
          "border border-[#D4D8DE] bg-white text-[#5A6B7D] hover:bg-[rgba(9,21,37,0.05)]",
        secondary:
          "border border-[#D4D8DE] bg-white text-[#5A6B7D] hover:bg-[rgba(9,21,37,0.05)]",
        ghost: "bg-transparent hover:bg-[rgba(9,21,37,0.05)]",
        link: "text-[color:var(--eq-gold-deep)] underline decoration-[rgba(199,168,79,0.4)] underline-offset-4 hover:decoration-[color:var(--eq-gold-deep)]",
        // On a navy surface: hairline white border, white text; the gold ring still shows.
        onDark:
          "border border-[rgba(255,255,255,0.18)] bg-transparent text-white hover:bg-[rgba(255,255,255,0.08)]",
        // The gold action on a navy header — the same as `gold`, named for the reader.
        onDarkGold: "eq-btn-gold bg-[color:var(--eq-gold)] text-[color:var(--eq-navy)]",
      },
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        xs: "h-6 gap-1 px-2 text-xs has-[>svg]:px-1.5 [&_svg:not([class*='size-'])]:size-3",
        sm: "h-8 gap-1.5 px-3 has-[>svg]:px-2.5",
        lg: "h-10 px-6 has-[>svg]:px-4",
        icon: "size-9",
        "icon-xs": "size-6 [&_svg:not([class*='size-'])]:size-3",
        "icon-sm": "size-8",
        "icon-lg": "size-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

function Button({
  className,
  variant = "default",
  size = "default",
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot : "button"

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      data-destructive={variant === "destructive" ? "true" : undefined}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export { Button, buttonVariants }
